import { useEffect, useMemo, useRef, useState } from 'react';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Stack } from '@mui/material';
import { RemoteAppErrorBoundary } from '../RemoteAppErrorBoundary';
import { useModuleLoader } from '../../hooks/useModuleLoader';
import {
  useShellBridgeMount,
  type Auth0Ref,
  type PreBridgeMountArgs,
} from '../../hooks/useShellBridgeMount';
import type {
  EntityProvider,
  NavigationProvider,
} from '../../services/shellBridge/createShellBridge';
import { useCurrentPath } from '../../hooks/useCurrentPath';
import { useExternalLinkTracking } from '../../hooks/useExternalLinkTracking';
import { useEntity } from '../../contexts/EntityContext';
import { usePspContext } from '../../contexts/PspContext';
import { useAuth } from '../../hooks/useAuth';
import { RemoteModuleWandLoader } from '../RemoteModuleWandLoader';
import { LoadingErrorPage } from '../../pages/LoadingErrorPage';
import type { EntityBase, HostingEnvironment } from '@opengov/psp-contract';
import type { PspModuleConfigContext, RemoteModuleConfig } from '../../types';
import { resolveModuleUrl } from '../../services/moduleResolver';
import { resolveDeepLinkNavigation, performDeepLinkNavigation } from '../../utils/urlHelpers';
import { auth0Config } from '../../utils';
import { detectPspHostingEnvironment } from '../../services/utils';
import { TestIds } from '../../constants';
import { useFeatureFlag, FeatureFlagKey } from '../../hooks/useFeatureFlags';
import { ShellRemoteNavBar } from './ShellRemoteNavBar';

interface RemoteAppShellProps {
  /**
   * Configuration for the remote module to load.
   * Accepts either app registry entries or route registry entries.
   */
  moduleConfig: RemoteModuleConfig;
  showDebugInfo?: boolean;
  /**
   * Called once after commit when module load/config/mount fails.
   * Optional — e.g. onboarding escape hatch to avoid locking users on a bad deploy.
   */
  onModuleError?: (error: Error) => void;
  /** When true, renders as a fixed-position full-viewport overlay (position: fixed; inset: 0). */
  fullPage?: boolean;
}

const normalizeBasePath = (p: string) => p.replace(/\/+$/, '') || '/';

export function RemoteAppShell({
  moduleConfig,
  showDebugInfo,
  onModuleError,
  fullPage = false,
}: RemoteAppShellProps) {
  // Track current pathname (captures all URL changes, including from remote apps)
  // React Router's useLocation hook is not used here because it does not capture URL changes from remote apps.
  const currentPath = useCurrentPath();

  // Shell-side fallback that remounts the module when a PSP-initiated navigation
  // lands anywhere within this app's path space (base or deep sub-path). Covers
  // sidebar re-select (lands on basePath) and favorite clicks that target a deep
  // path within the same module.
  //
  // MUST filter to PUSH/REPLACE (PSP-driven `navigate()` calls). POP navigations
  // include browser back/forward AND popstate events that a remote module may
  // dispatch after its own internal `pushState` — treating those as remount
  // triggers loops the module indefinitely: mount → module dispatches popstate →
  // key changes → unmount → remount → repeat (PSS-2680 regression).
  const location = useLocation();
  const navigationType = useNavigationType();
  const normalizedPath = normalizeBasePath(location.pathname);
  const normalizedBase = normalizeBasePath(moduleConfig.basePath);
  const withinBase =
    normalizedPath === normalizedBase || normalizedPath.startsWith(normalizedBase + '/');
  const [seenNavKey, setSeenNavKey] = useState(location.key);
  const [baseResetToken, setBaseResetToken] = useState(0);
  if (location.key !== seenNavKey) {
    setSeenNavKey(location.key);
    if (withinBase && navigationType !== NavigationType.Pop) {
      setBaseResetToken((n) => n + 1);
    }
  }

  const hostingEnvironment: HostingEnvironment = detectPspHostingEnvironment();
  const shellRemoteNavEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_SHELL_REMOTE_NAV);
  const {
    activeEntity,
    activeEntityEntitledApplications,
    entityProductEntitlements,
    setActiveEntityByFriendlyId,
  } = useEntity();

  const entitledProductKeys = useMemo(
    () =>
      activeEntityEntitledApplications
        .map((entitlement) => entitlement.entitlementProductKey)
        .sort(),
    [activeEntityEntitledApplications]
  );
  const entityEntitledProductKeys = useMemo(
    () => entityProductEntitlements.map((entitlement) => entitlement.entitlementProductKey).sort(),
    [entityProductEntitlements]
  );
  const { appRegistry } = usePspContext();
  const navigate = useNavigate();
  const {
    token,
    user,
    isLoading: authLoading,
    isAuthenticated,
    error: authError,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
  } = useAuth();
  const [resolvedModuleUrl, setResolvedModuleUrl] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Store latest values in refs to avoid triggering re-mounts
  // These refs are updated on every render but don't cause effect re-runs
  const moduleMountArgsRef = useRef<PreBridgeMountArgs | null>(null);
  const moduleConfigRef = useRef(moduleConfig);
  const resolvedModuleUrlRef = useRef(resolvedModuleUrl);
  // The registry route the module URL was resolved from. A ref, not state: the mount hook reads
  // it for sign-out bookkeeping, nothing renders it, and changing it must not remount.
  const resolvedModuleRoutePatternRef = useRef<string | null>(null);
  const showDebugInfoRef = useRef(showDebugInfo);
  // Auth ref — the shell bridge reads through this on every call so the mounted
  // module sees live auth state without remounting on token refresh / login flips.
  const auth0Ref = useRef<Auth0Ref | null>(null);
  // Navigation ref — lets `bridge.navigateToDeepLink` resolve a task/notification
  // deep link via the same app-registry logic as PSP's own TaskBar, without
  // remounting the module when appRegistry/navigate identity changes.
  const navigationProviderRef = useRef<NavigationProvider | null>(null);
  // Entity provider ref — lets `bridge.setActiveEntity` switch the shell's active
  // entity (for modules that encode the entity in their own URL, e.g. PLC) without
  // remounting the module when the EntityContext callback identity changes.
  const entityProviderRef = useRef<EntityProvider | null>(null);

  const { module, loading, error, reload, retryAttempt, maxRetries } =
    useModuleLoader(resolvedModuleUrl);

  // Surface retry progress to the user during transient-failure recovery.
  // The initial spinner stays unadorned; only retry phases get the
  // "Retrying… (attempt N of 3)" status line below the spinner.
  const retryMessage =
    retryAttempt > 0 ? `Retrying… (attempt ${retryAttempt} of ${maxRetries})` : undefined;

  // Enrich auth0Config with the shell's token-fetcher so modules can route
  // token requests through the shell's single Auth0 client instead of standing
  // up their own. The contract field is `getAccessToken` (renamed
  // from `getAccessTokenSilently` in psp-contract 1.0.0); the shell-side
  // variable from useAuth() keeps the SDK's full name. The static `token`
  // field stays as a backward-compat handoff during the migration window.
  const auth0ConfigWithToken = useMemo(
    () => ({
      ...auth0Config,
      ...(token ? { token } : {}),
      getAccessToken: getAccessTokenSilently,
    }),
    [token, getAccessTokenSilently]
  );

  // Lazily resolve moduleConfig — only fires when the user first navigates to this app.
  // Cached per (appId, entity) so navigating away and back doesn't re-fetch.
  // entityEntitledProductKeys is in the key so a changed entity entitlement (e.g. recovery
  // after a failed first fetch) re-resolves the config instead of serving a stale (and
  // persisted) answer. Failed rechecks keep the previous value, so this only remounts a
  // live module when the entitlement genuinely changes.
  const {
    data: resolvedRegistryConfig,
    isLoading: isConfigQueryLoading,
    error: configError,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: [
      'moduleConfig',
      moduleConfig.basePath,
      activeEntity?.friendlyId,
      entitledProductKeys,
      entityEntitledProductKeys,
    ],
    queryFn: async () => {
      const callbackContext: PspModuleConfigContext = {
        hostingEnvironment,
        auth0Config: auth0ConfigWithToken,
        activeEntity: activeEntity || undefined,
        user: user ? { email: user.email!, sub: user.sub!, name: user.name } : undefined,
        entitledProductKeys,
        entityEntitledProductKeys,
      };
      const result = await moduleConfig.moduleConfig!(callbackContext);

      // Validate result: if it's an empty object, treat as error
      // This prevents caching failures when entity wasn't loaded or config fetch failed
      if (Object.keys(result).length === 0) {
        throw new Error('moduleConfig returned empty object - entity may not be loaded yet');
      }

      return result;
    },
    enabled: !!activeEntity && !!token && !!moduleConfig.moduleConfig,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Only retry if empty object (entity not loaded yet), up to 3 times
      // Don't retry other errors (network failures, etc)
      return error.message.includes('empty object') && failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
  const isConfigLoading = !!moduleConfig.moduleConfig && isConfigQueryLoading;

  // Build PSP context for module resolution
  const moduleMountArgs: PreBridgeMountArgs | null = useMemo(() => {
    // If moduleConfig function exists but config isn't loaded yet, return null
    // This prevents mounting with empty config
    if (moduleConfig.moduleConfig && !resolvedRegistryConfig) {
      return null;
    }

    // Deep-freeze registryConfig to prevent mutations and ensure immutability per mount
    const frozenConfig = Object.freeze(JSON.parse(JSON.stringify(resolvedRegistryConfig ?? {})));

    return {
      baseRouterPath: moduleConfig.basePath,
      auth0Config: auth0ConfigWithToken,
      activeEntity: activeEntity as EntityBase,
      hostingEnvironment,
      registryConfig: frozenConfig,
    };
  }, [
    hostingEnvironment,
    moduleConfig.basePath,
    activeEntity,
    auth0ConfigWithToken,
    resolvedRegistryConfig,
    moduleConfig.moduleConfig,
  ]);

  // Update refs on every render to capture latest values
  // This ensures cleanup and mount always use current values
  // without triggering unnecessary re-renders
  useEffect(() => {
    moduleMountArgsRef.current = moduleMountArgs;
    moduleConfigRef.current = moduleConfig;
    resolvedModuleUrlRef.current = resolvedModuleUrl;
    showDebugInfoRef.current = showDebugInfo;
    auth0Ref.current = {
      isAuthenticated,
      isLoading: authLoading,
      user,
      error: authError,
      getAccessTokenSilently,
      getIdTokenClaims,
      logout,
    };
    navigationProviderRef.current = {
      navigateToDeepLink: (deeplinkUrl, subject) =>
        performDeepLinkNavigation(
          resolveDeepLinkNavigation(deeplinkUrl, subject, appRegistry),
          navigate
        ),
    };
    entityProviderRef.current = {
      setActiveEntity: (friendlyId) => setActiveEntityByFriendlyId(friendlyId),
    };
  });

  useEffect(() => {
    // Add PSP context to window object for debugging purposes.
    // Strip token and live token-fetcher from auth0Config before exposing on
    // window — both are security hazards readable by any JS on the page (XSS,
    // extensions, console). Modules MUST use options.auth0Config from mount().
    const { auth0Config: rawAuth0Config, ...restMountArgs } = moduleMountArgs ?? {};
    const { token, getAccessToken, ...safeAuth0Config } = rawAuth0Config ?? {};
    void token;
    void getAccessToken; // intentionally stripped from window exposure
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).opengovConfig = {
      ...restMountArgs,
      auth0Config: safeAuth0Config,
      // Duplicating to maintain backward compatibility with existing code.
      auth: { ...auth0Config, token: '', getAccessToken: undefined },
    };
  }, [moduleMountArgs]);

  // Resolve remote app module URL based on current route and context
  // Supports loading sub-modules dynamically based on URL patterns defined in the app registry.
  useEffect(() => {
    let mounted = true;

    resolveModuleUrl(currentPath, moduleConfig)
      .then(({ url, route }) => {
        if (mounted) resolvedModuleRoutePatternRef.current = route.pattern;
        if (mounted && url !== resolvedModuleUrl) {
          console.log(`[RemoteAppShell] Setting new resolved module URL: ${url}`);
          setResolvedModuleUrl(url);
        }
      })
      .catch((err) => {
        console.error(`[RemoteAppShell] Failed to resolve module URL for ${moduleConfig.id}:`, err);
        if (mounted) {
          setResolvedModuleUrl(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [moduleConfig, currentPath, moduleMountArgs, resolvedModuleUrl]);

  // Mount/unmount lifecycle — delegates bridge creation and disposal to useShellBridgeMount.
  // CRITICAL: Only remount when module, activeEntity, or resolved config changes.
  // configReadyDep blocks mounting until moduleConfig resolves (undefined while pending,
  // truthy once resolved). Apps without a moduleConfig function use a constant sentinel.
  const configReadyDep = moduleConfig.moduleConfig ? resolvedRegistryConfig : 'no-config';

  const { mountError, retryMount } = useShellBridgeMount({
    module,
    containerRef,
    mountArgsRef: moduleMountArgsRef,
    auth0Ref,
    remountDeps: [activeEntity, configReadyDep, baseResetToken],
    appConfig: moduleConfig,
    hostingEnvironment,
    showDebugInfoRef,
    resolvedModuleUrlRef,
    resolvedModuleRoutePatternRef,
    navigationProviderRef,
    entityProviderRef,
    claimActiveRemoteNav: true,
  });

  // Track when remote modules contain links that navigate outside PSP context
  useExternalLinkTracking({
    containerRef,
    moduleId: moduleConfig.id,
    basePath: moduleConfig.basePath,
    enabled: !!module && !loading && !error,
  });

  // activeError precedence is `error > configError > mountError`. Mount errors
  // cannot coexist with load errors (the hook bails on `module == null`) or with
  // config errors (mountArgs is null while config is missing), but appending
  // mountError makes the error surfaceable on its own.
  //
  // handleRetry follows the SAME precedence so the retry button always operates
  // on the error the user is reading. If priorities diverged, a render→effect
  // race could leave stale mountError attached to a click that should have called
  // reload() — and the retry would silently no-op.
  const activeError = error || configError || mountError;
  const handleRetry = error ? reload : configError ? () => refetchConfig() : retryMount;

  const isShellLoading = loading || authLoading || !activeEntity || isConfigLoading;

  // Notify optional error listener after commit — never during render (render purity).
  // Ref ensures the callback fires at most once per shell instance.
  const didNotifyErrorRef = useRef(false);
  useEffect(() => {
    if (activeError && onModuleError && !didNotifyErrorRef.current) {
      didNotifyErrorRef.current = true;
      onModuleError(activeError instanceof Error ? activeError : new Error(String(activeError)));
    }
  }, [activeError, onModuleError]);

  const shell = (
    <RemoteAppErrorBoundary>
      {/* Show wand loader while remote module loads */}
      {isShellLoading && (
        <Box
          data-testid={TestIds.LOADING_SPINNER}
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <RemoteModuleWandLoader message={retryMessage} />
        </Box>
      )}

      {/* Show error page */}
      {activeError && (
        <Box
          data-testid={TestIds.ERROR_PAGE}
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <LoadingErrorPage error={activeError} onRetry={handleRetry} />
        </Box>
      )}

      {/* Shell-owned NavBar + remote mount container. NavBar reads the active
          routed app's remoteNav contribution; hidden until config is pushed.

          NavBar is wrapped in its own error boundary to prevent throws from bad
          remoteNav data from unmounting the sibling module container without
          calling module.unmount(). If NavBar fails, we show its fallback instead
          of taking down the healthy module. */}
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          display: isShellLoading || activeError ? 'none' : 'flex',
          overflow: 'hidden',
        }}
      >
        {shellRemoteNavEnabled && (
          <RemoteAppErrorBoundary>
            <ShellRemoteNavBar />
          </RemoteAppErrorBoundary>
        )}
        <Box
          data-testid={TestIds.REMOTE_APP_CONTAINER}
          id="psp-remote-app-shell-container"
          ref={containerRef}
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        />
      </Stack>
    </RemoteAppErrorBoundary>
  );

  if (fullPage) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal,
          width: '100%',
          height: '100%',
          overflow: 'auto',
          backgroundColor: 'background.paper',
        }}
      >
        {shell}
      </Box>
    );
  }

  // Fill Layout <main> height so loading/error states can center vertically.
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      {shell}
    </Box>
  );
}
